import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/20/solid';

const highlights = [
  'Extract structured data from PDFs, scans and images in seconds',
  'Classify documents and route them into the right extraction',
  'REST API, webhooks, Zapier and MCP for your own workflows',
];

/**
 * AuthLayout: split screen used by Login / Register / Forgot / Verify.
 */
export default function AuthLayout({ title, description, children, footer }) {
  return (
    <div className="flex min-h-full bg-gray-50">
      {/* Brand panel */}
      <aside className="relative hidden w-[46%] max-w-2xl flex-col justify-between overflow-hidden bg-indigo-600 p-10 text-white lg:flex xl:p-14">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-indigo-400/40 blur-3xl" />
          <div className="absolute -bottom-32 -right-16 h-[28rem] w-[28rem] rounded-full bg-violet-500/40 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_45%)]" />
        </div>

        <div className="relative">
          <Link to="/login" className="inline-flex rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
            <img className="h-8 w-auto" src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'} alt="Extracta LABS" />
          </Link>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight xl:text-4xl">Turn documents into structured data.</h1>
          <p className="mt-4 text-base text-indigo-100">AI document extraction and classification for teams that need clean data, not more paperwork.</p>
          <ul className="mt-8 space-y-3">
            {highlights.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-indigo-50">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-inset ring-white/20">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-indigo-200">© {new Date().getFullYear()} Extracta LABS · extracta.ai</p>
      </aside>

      {/* Form panel */}
      <main className="flex min-h-full flex-1 flex-col justify-center px-4 py-10 sm:px-6 lg:px-12">
        <div className="mx-auto w-full max-w-[26rem]">
          <div className="mb-8 lg:hidden">
            <img className="h-8 w-auto" src={process.env.PUBLIC_URL + '/logo_extracta_labs_main.png'} alt="Extracta LABS" />
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-card ring-1 ring-gray-200 sm:p-8 animate-fade-up">
            {title && <h2 className="text-xl font-semibold tracking-tight text-gray-900">{title}</h2>}
            {description && <p className="mt-1.5 text-sm text-gray-500">{description}</p>}
            <div className="mt-6">{children}</div>
          </div>

          {footer && <div className="mt-6 text-center text-sm text-gray-500">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function AuthDivider({ children = 'or' }) {
  return (
    <div className="relative my-5" aria-hidden="true">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-200" />
      </div>
      <div className="relative flex justify-center text-xs">
        <span className="bg-white px-3 uppercase tracking-wider text-gray-400">{children}</span>
      </div>
    </div>
  );
}
